"use client";

import {
  Activity,
  ArrowRight,
  BarChart3,
  Bell,
  Clock,
  DollarSign,
  GitBranch,
  Paintbrush,
  RotateCcw,
  Shield,
  Webhook,
  Workflow,
  Zap,
} from "lucide-react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from "@/components/ui/motion";

const mainFeatures = [
  {
    icon: Paintbrush,
    title: "Visual workflow builder",
    description:
      "Build complex automations with a drag-and-drop interface. No code required, but code-friendly when you need it. Design branching logic, loops, and conditional flows visually.",
    highlights: ["Drag & drop nodes", "Visual branching", "Code when needed"],
  },
  {
    icon: BarChart3,
    title: "Full observability",
    description:
      "Real-time execution logs for every workflow run. Searchable history, instant debugging, and complete visibility into what's happening at every step.",
    highlights: ["Real-time logs", "Searchable history", "Instant debugging"],
  },
  {
    icon: RotateCcw,
    title: "Built-in retries & recovery",
    description:
      "Automatic retries with exponential backoff. Your workflows recover gracefully from transient failures without manual intervention.",
    highlights: ["Auto-retry", "Exponential backoff", "Graceful recovery"],
  },
  {
    icon: Webhook,
    title: "Webhooks & HTTP",
    description:
      "Send and receive webhooks from any service. Custom HTTP requests when you need them. Connect to any API in minutes.",
    highlights: ["Inbound webhooks", "HTTP requests", "Any API"],
  },
];

const additionalFeatures = [
  {
    icon: Clock,
    title: "Scheduled triggers",
    description: "Run workflows on any schedule",
  },
  {
    icon: GitBranch,
    title: "Version control",
    description: "Track changes and roll back",
  },
  {
    icon: Bell,
    title: "Smart alerts",
    description: "Get notified when things fail",
  },
  { icon: Zap, title: "Fast execution", description: "Sub-50ms latency" },
  {
    icon: Shield,
    title: "Enterprise security",
    description: "SOC 2 compliant infrastructure",
  },
  {
    icon: DollarSign,
    title: "Transparent pricing",
    description: "No per-task surprises",
  },
];

function MiniWorkflow() {
  return (
    <div className="flex items-center gap-2 mt-6">
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
        <div className="w-2 h-2 rounded-full bg-success" />
        <span className="text-xs font-medium text-foreground">Trigger</span>
      </div>
      <div className="w-6 h-px bg-border" />
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted border border-border">
        <Workflow className="w-3 h-3 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          Process
        </span>
      </div>
      <div className="w-6 h-px bg-border" />
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted border border-border">
        <span className="text-xs font-medium text-muted-foreground">
          Action
        </span>
      </div>
    </div>
  );
}

function MiniLogViewer() {
  return (
    <div className="mt-6 p-3 rounded-lg bg-background/80 border border-border font-mono text-xs space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-success">✓</span>
        <span className="text-muted-foreground">10:32:18</span>
        <span className="text-foreground">Trigger received</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-success">✓</span>
        <span className="text-muted-foreground">10:32:19</span>
        <span className="text-foreground">Data processed</span>
      </div>
      <div className="flex items-center gap-2">
        <Activity className="w-3 h-3 text-primary animate-pulse" />
        <span className="text-muted-foreground">10:32:20</span>
        <span className="text-primary">Running...</span>
      </div>
    </div>
  );
}

export default function FeaturesPage() {
  const navigate = useRouter();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-20 pointer-events-none" />
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <FadeInUp className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1.5 mb-4 text-xs font-semibold tracking-wider uppercase bg-primary/10 text-primary rounded-full border border-primary/20">
              Features
            </span>
            <h1 className="text-foreground mb-6">
              Everything you need to
              <br />
              <span className="gradient-text">automate with confidence</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Build, debug, and monitor production workflows with tools designed
              for reliability. From simple automations to complex integrations.
            </p>
            <Button
              variant="gradient"
              size="lg"
              onClick={() => navigate.push("/register")}
            >
              Start building free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </FadeInUp>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {mainFeatures.map((feature, index) => (
              <StaggerItem key={index}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="h-full p-8 rounded-2xl card-interactive"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {feature.highlights.map((highlight, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 text-xs font-medium bg-muted rounded-full text-muted-foreground"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                  {index === 0 && <MiniWorkflow />}
                  {index === 1 && <MiniLogViewer />}
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="text-center mb-14">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              And much more
            </h2>
            <p className="text-lg text-muted-foreground">
              Every feature you need for production-grade workflows
            </p>
          </FadeInUp>

          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {additionalFeatures.map((feature, index) => (
              <StaggerItem key={index}>
                <motion.div
                  whileHover={{ y: -2 }}
                  className="flex items-start gap-4 p-5 rounded-xl card-interactive"
                >
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Ready to get started?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start building your first workflow in minutes. Free tier included.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="gradient"
                size="lg"
                onClick={() => navigate.push("/register")}
              >
                Start building free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate.push("/templates")}
              >
                Browse templates
              </Button>
            </div>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
}
